import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setDialogOpen, setSelectedEvent } from '../../store/Actions';
import { buildDetailPath, buildSectionPath, useAppNavigate } from '../../utils/Routing';
import { MyAvatar } from '../common/MyAvatar';
import {
  HeroBar, StatTile, YearChart,
  CategoryRankRow, FriendRankRow, EventRow,
} from './DetailViewParts';
import { EntityEvents } from './EntityEvents';
import {
  buildYearBreakdown, findMostActiveYear, rankByEvents,
  findTopPlace, buildRecentEvents, getEventStartAt, formatLongDate,
} from './insightHelpers';
import './DetailView.scss';

export function FriendDetailView({entityId, tab = 'summary'}) {
  const friends = useSelector(state => state.viewingUserFriends) || {};
  const categories = useSelector(state => state.viewingUserCategories) || {};
  const trips = useSelector(state => state.viewingUserTrips) || {};
  const events = useSelector(state => state.viewingUserEvents) || {};
  const viewingUserAccount = useSelector(state => state.viewingUserAccount);
  const loggedInUserAccount = useSelector(state => state.loggedInUserAccount);
  const isOwnMap = viewingUserAccount && loggedInUserAccount
    && viewingUserAccount.id === loggedInUserAccount.id;
  const dispatch = useDispatch();
  const navigateTo = useAppNavigate();

  const friend = friends[entityId];
  const username = viewingUserAccount?.username;

  // If the friend isn't found (deleted, bad URL), bounce back to the list.
  useEffect(() => {
    if(!friend && username){
      navigateTo(buildSectionPath(username, 'Friends'));
    }
  }, [friend, username, navigateTo]);

  // Events tagged with this friend — the canvas for every insight below.
  const friendEvents = useMemo(() => {
    return Object.values(events).filter(e => (e.friendIds || []).includes(entityId));
  }, [events, entityId]);

  const yearEntries = useMemo(() => buildYearBreakdown(friendEvents), [friendEvents]);
  const mostActiveYear = useMemo(() => findMostActiveYear(friendEvents), [friendEvents]);
  const topCategories = useMemo(
    () => rankByEvents(friendEvents, e => e.categoryId, categories),
    [friendEvents, categories]
  );
  const topCoFriends = useMemo(() => {
    return rankByEvents(
      friendEvents,
      e => (e.friendIds || []).filter(id => id !== entityId),
      friends
    );
  }, [friendEvents, friends, entityId]);
  const topTrips = useMemo(
    () => rankByEvents(friendEvents, e => e.tripIds, trips),
    [friendEvents, trips]
  );
  const topPlace = useMemo(() => findTopPlace(friendEvents), [friendEvents]);
  const recentEvents = useMemo(() => buildRecentEvents(friendEvents, 6), [friendEvents]);

  // Friend's first/most-recent shared memory.
  const sortedDates = useMemo(() => {
    return friendEvents.map(getEventStartAt).filter(Boolean).sort((a, b) => a - b);
  }, [friendEvents]);
  const firstDate = sortedDates[0] || null;
  const lastDate = sortedDates[sortedDates.length - 1] || null;
  const yearsCovered = firstDate && lastDate
    ? Math.max(1, Math.round((lastDate - firstDate) / (365.25 * 24 * 60 * 60 * 1000)))
    : 0;

  if(!friend){
    return (
      <main className="detail-view">
        <div className="detail-view__not-found">
          <div>This friend couldn't be found.</div>
        </div>
      </main>
    );
  }

  const goBack = () => {
    if(!username) return;
    navigateTo(buildSectionPath(username, 'Friends'));
  };

  const selectTab = (nextTab) => {
    if(!username || nextTab === tab) return;
    navigateTo(buildDetailPath(username, 'Friends', friend.id, nextTab));
  };

  const openEdit = () => {
    dispatch(setDialogOpen('Edit Friend', {
      parentDialog: '',
      listDialog: null,
      dialogTitle: `Edit Friend: ${friend.name}`,
      friend,
    }));
  };

  // New event pre-tagged with this friend, opening straight into the
  // Edit Event form. Mirrors AddEventButton's "use the first category"
  // fallback so the new event has a valid category id from the start.
  const openNewEvent = () => {
    if(!loggedInUserAccount) return;
    const categoryList = Object.values(categories);
    if(categoryList.length === 0){
      alert('Create a category first.');
      return;
    }
    const newEvent = {
      userId: loggedInUserAccount.id,
      title: '',
      categoryId: categoryList[0].id,
      friendIds: [friend.id],
    };
    dispatch(setDialogOpen('Edit Event', {event: newEvent}));
  };

  const openEvent = (event) => {
    dispatch(setSelectedEvent(event));
    dispatch(setDialogOpen('View Event', {event}));
  };

  const openTrip = (trip) => {
    if(!username) return;
    navigateTo(`/${encodeURIComponent(username)}/trips/${encodeURIComponent(trip.id)}`);
  };

  const openCategory = (category) => {
    if(!username) return;
    navigateTo(`/${encodeURIComponent(username)}/categories/${encodeURIComponent(category.id)}`);
  };

  const openFriend = (other) => {
    if(!username) return;
    navigateTo(`/${encodeURIComponent(username)}/friends/${encodeURIComponent(other.id)}`);
  };

  return (
    <main className={`detail-view ${tab === 'events' ? 'detail-view--events' : ''}`.trim()}>
      <section className={`detail-view__hero ${tab === 'events' ? 'detail-view__hero--collapsed' : ''}`.trim()}>
        <div className="detail-view__hero-glow detail-view__hero-glow--friend" aria-hidden="true"/>
        <HeroBar
          backLabel="All Friends"
          onBack={goBack}
          onNewEvent={isOwnMap ? openNewEvent : undefined}
          onEdit={isOwnMap ? openEdit : undefined}
          editLabel="Edit Friend"
          entityTitle={friend.name}
          tab={tab}
          onSelectTab={selectTab}
        />
        <div className="detail-view__hero-content">
          <div style={{animation:'detail-icon-pop 700ms cubic-bezier(.21,.99,.32,1) both'}}>
            <MyAvatar
              userAccount={friend}
              style={{height:104, width:104, fontSize:38}}
            />
          </div>
          <div className="detail-view__hero-tagline">Friend</div>
          <h1 className="detail-view__hero-title">{friend.name}</h1>
          <div className="detail-view__hero-subtitle">
            {friendEvents.length > 0
              ? `${friendEvents.length} ${friendEvents.length === 1 ? 'shared memory' : 'shared memories'}${yearsCovered > 0 ? ` over ${yearsCovered} ${yearsCovered === 1 ? 'year' : 'years'}` : ''}`
              : 'No shared memories yet'}
          </div>
        </div>
      </section>

      {tab === 'events' &&
        <EntityEvents
          events={friendEvents}
          emptyText={`Tag ${friend.name} in an event to start building a shared timeline.`}
        />
      }

      {tab === 'summary' &&
      <>
      <section className="detail-view__stats-grid" aria-label="Friend stats">
        <StatTile
          label="Events together"
          value={friendEvents.length}
          accent="#f59e0b"
          icon="📅"
          delay={120}
        />
        <StatTile
          label="Trips together"
          value={topTrips.length}
          accent="#0ea5e9"
          icon="🧳"
          delay={200}
        />
        <StatTile
          label="Years known"
          value={yearsCovered}
          accent="#8b5cf6"
          icon="⏳"
          delay={280}
        />
        <StatTile
          label="Categories"
          value={topCategories.length}
          accent="#10b981"
          icon="🗂️"
          delay={360}
        />
      </section>

      {yearEntries.length > 0 &&
        <section className="detail-view__panel">
          <header className="detail-view__panel-header">
            <h2>Events by Year</h2>
            {mostActiveYear && mostActiveYear[1] > 0 &&
              <span className="detail-view__panel-subtitle">
                {mostActiveYear[0]} was your most-shared year ({mostActiveYear[1]} {mostActiveYear[1] === 1 ? 'event' : 'events'})
              </span>
            }
          </header>
          <YearChart entries={yearEntries} accentTop="#fbbf24" accentBottom="#f59e0b"/>
        </section>
      }

      {(topCategories.length > 0 || topCoFriends.length > 0) &&
        <section className="detail-view__two-up">
          {topCategories.length > 0 &&
            <div className="detail-view__panel">
              <header className="detail-view__panel-header">
                <h2>Top Categories</h2>
              </header>
              <div className="detail-view__rank-list">
                {topCategories.map((item, idx) => (
                  <CategoryRankRow
                    key={item.entity.id}
                    category={item.entity}
                    count={item.count}
                    percent={(item.count / topCategories[0].count) * 100}
                    delay={400 + idx * 80}
                    onClick={() => openCategory(item.entity)}
                  />
                ))}
              </div>
            </div>
          }
          {topCoFriends.length > 0 &&
            <div className="detail-view__panel">
              <header className="detail-view__panel-header">
                <h2>Most Often With</h2>
              </header>
              <div className="detail-view__rank-list">
                {topCoFriends.map((item, idx) => (
                  <FriendRankRow
                    key={item.entity.id}
                    friend={item.entity}
                    count={item.count}
                    percent={(item.count / topCoFriends[0].count) * 100}
                    delay={500 + idx * 80}
                    onClick={() => openFriend(item.entity)}
                  />
                ))}
              </div>
            </div>
          }
        </section>
      }

      {(topTrips.length > 0 || topPlace) &&
        <section className="detail-view__two-up">
          {topTrips.length > 0 &&
            <div className="detail-view__panel">
              <header className="detail-view__panel-header">
                <h2>Top Trips Together</h2>
              </header>
              <div className="detail-view__rank-list">
                {topTrips.map((item, idx) => (
                  <button
                    key={item.entity.id}
                    type="button"
                    className="detail-view__rank-row detail-view__rank-row--clickable"
                    style={{'--row-delay': `${600 + idx * 80}ms`, '--bar-target-width': `${(item.count / topTrips[0].count) * 100}%`}}
                    onClick={() => openTrip(item.entity)}
                  >
                    <div
                      className="detail-view__rank-icon"
                      style={{backgroundColor: '#0ea5e9'}}
                    >🧳</div>
                    <div className="detail-view__rank-body">
                      <div className="detail-view__rank-label-row">
                        <span className="detail-view__rank-name">{item.entity.name}</span>
                        <span className="detail-view__rank-count">{item.count}</span>
                      </div>
                      <div className="detail-view__rank-bar">
                        <div
                          className="detail-view__rank-bar-fill"
                          style={{background: 'linear-gradient(90deg, #0ea5e9, #6366f1)'}}
                        />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          }
          {topPlace &&
            <div className="detail-view__panel">
              <header className="detail-view__panel-header">
                <h2>Highlights</h2>
              </header>
              <div className="detail-view__rank-list">
                {firstDate &&
                  <div className="detail-view__rank-row">
                    <div className="detail-view__rank-icon" style={{backgroundColor:'#10b981'}}>✨</div>
                    <div className="detail-view__rank-body">
                      <div className="detail-view__rank-label-row">
                        <span className="detail-view__rank-name">First memory</span>
                        <span className="detail-view__rank-count">{formatLongDate(firstDate)}</span>
                      </div>
                    </div>
                  </div>
                }
                {lastDate && lastDate.getTime() !== firstDate?.getTime() &&
                  <div className="detail-view__rank-row">
                    <div className="detail-view__rank-icon" style={{backgroundColor:'#f59e0b'}}>🕒</div>
                    <div className="detail-view__rank-body">
                      <div className="detail-view__rank-label-row">
                        <span className="detail-view__rank-name">Most recent</span>
                        <span className="detail-view__rank-count">{formatLongDate(lastDate)}</span>
                      </div>
                    </div>
                  </div>
                }
                {topPlace &&
                  <div className="detail-view__rank-row">
                    <div className="detail-view__rank-icon" style={{backgroundColor:'#6366f1'}}>📍</div>
                    <div className="detail-view__rank-body">
                      <div className="detail-view__rank-label-row">
                        <span className="detail-view__rank-name">Most-visited place</span>
                        <span className="detail-view__rank-count">{topPlace.count}</span>
                      </div>
                      <div className="detail-view__event-meta" style={{marginTop:0}}>
                        {topPlace.description}
                      </div>
                    </div>
                  </div>
                }
              </div>
            </div>
          }
        </section>
      }

      {recentEvents.length > 0 &&
        <section className="detail-view__panel">
          <header className="detail-view__panel-header">
            <h2>Recent Events Together</h2>
            <span className="detail-view__panel-subtitle">
              {friendEvents.length} total
            </span>
          </header>
          <div className="detail-view__events-list">
            {recentEvents.map((item, idx) => (
              <EventRow
                key={item.event.id}
                event={item.event}
                startAt={item.startAt}
                category={categories[item.event.categoryId]}
                onClick={() => openEvent(item.event)}
                delay={120 + idx * 50}
              />
            ))}
          </div>
        </section>
      }

      {friendEvents.length === 0 &&
        <section className="detail-view__empty">
          <div className="detail-view__empty-title">No shared memories yet</div>
          <div>Tag {friend.name} in an event to start building a shared timeline.</div>
        </section>
      }
      </>
      }
    </main>
  );
}
